import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, LinearProgress, Typography, Alert } from '@mui/material';

interface GrouperUploadProgressProps {
  open: boolean;
  progress: number;
  status: string;
  error?: string;
  success?: boolean;
  uploadCount?: number;
  totalCount?: number;
  onClose?: () => void;
}

const GrouperUploadProgress: React.FC<GrouperUploadProgressProps> = ({
  open,
  progress,
  status,
  error,
  success,
  uploadCount,
  totalCount,
  onClose
}) => {
  const getProgressColor = () => {
    if (error) return 'error';
    if (success) return 'success';
    return 'primary';
  };

  const getProgressVariant = () => {
    if (progress === 0 && !error && !success) return 'indeterminate';
    return 'determinate';
  };

  return (
    <Dialog 
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: 200 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {error ? 'Upload Failed' : success ? 'Upload Complete' : 'Uploading Groupers'}
        {onClose && (
          <Button
            onClick={onClose}
            sx={{ minWidth: 'auto', p: 1 }}
            size="small"
          >
            ✕
          </Button>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress 
            variant={getProgressVariant()}
            value={progress}
            color={getProgressColor()}
            sx={{ height: 8, borderRadius: 4 }}
          />
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {status}
            </Typography>
            
            {uploadCount !== undefined && totalCount !== undefined && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Processed: {uploadCount.toLocaleString()} of {totalCount.toLocaleString()} records
              </Typography>
            )}
            
            {progress > 0 && !error && !success && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {Math.round(progress)}% complete
              </Typography>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2">
                {error}
              </Typography>
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Groupers have been successfully uploaded and are now available in the grid.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          size="large"
          disabled={!onClose}
          sx={{ minWidth: 120 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GrouperUploadProgress;
